import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import apiService from '../services/api';
import { ROUTES } from '../seo/routeMetadata';
import { SportMarketRow } from '../components/sports/SportMarketRow';

const SPORTS = { tennis: 'tennis', cricket: 'cricket', esports: 'esports' };
const OTHER_LINKS = [['tennis', 'Tennis'], ['cricket', 'Cricket'], ['esports', 'eSports']];

const useSportMarkets = (subcategory) => {
  const [state, setState] = useState({ markets: [], loading: true });
  useEffect(() => {
    let alive = true;
    setState({ markets: [], loading: true });
    apiService.getMarkets('OPEN', subcategory, null, 100)
      .then((d) => {
        const now = Date.now();
        const markets = (d.markets || [])
          .filter((m) => new Date(m.closes_at).getTime() > now)
          .sort((a, b) => new Date(a.closes_at) - new Date(b.closes_at));
        if (alive) setState({ markets, loading: false });
      })
      .catch(() => alive && setState({ markets: [], loading: false }));
    return () => { alive = false; };
  }, [subcategory]);
  return state;
};

const Faq = ({ faq }) => (
  <section className="mt-14 max-w-3xl" data-testid="sport-faq">
    <h2 className="text-base md:text-lg font-semibold text-white mb-4">Frequently asked questions</h2>
    {faq.map((f) => (
      <details key={f.q} className="group border-b border-[#1e2a45] py-4">
        <summary className="cursor-pointer text-sm text-white list-none flex justify-between">{f.q}<span className="text-gray-500 group-open:rotate-45 transition-transform">+</span></summary>
        <p className="mt-2 text-sm text-gray-400 leading-relaxed">{f.a}</p>
      </details>
    ))}
  </section>
);

export default function SportPage() {
  const { sport } = useParams();
  const sub = SPORTS[sport];
  const meta = ROUTES[`/sports/${sport}`];
  const { markets, loading } = useSportMarkets(sub);
  if (!sub || !meta) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#050810]" data-testid={`sport-page-${sport}`}>
      <SEO title={meta.title} description={meta.description} keywords={meta.keywords} url={`https://amppredict.com/sports/${sport}`} />
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-20">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3B82F6] mb-3">Sports · Auto-created every few hours</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight" data-testid="sport-h1">{meta.h1}</h1>
        <p className="mt-4 max-w-3xl text-base text-gray-300 leading-relaxed" data-testid="sport-intro">{meta.aeoAnswer}</p>
        <nav className="mt-6 flex flex-wrap gap-2" aria-label="Other sports">
          {OTHER_LINKS.map(([slug, name]) => (
            <Link key={slug} to={`/sports/${slug}`} data-testid={`sport-nav-${slug}`}
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${slug === sport ? 'border-[#3B82F6] bg-[#3B82F6]/15 text-white' : 'border-[#1e2a45] text-gray-400 hover:text-white'}`}>{name}</Link>
          ))}
          <Link to="/picks" data-testid="sport-nav-football" className="rounded-full border border-[#1e2a45] px-3 py-1 text-xs text-gray-400 hover:text-white transition-colors">Football picks</Link>
        </nav>
        <section className="mt-10" data-testid="sport-markets">
          <h2 className="text-base md:text-lg font-semibold text-white mb-2">
            Open markets <span className="text-gray-500 font-normal" data-testid="sport-market-count">{loading ? '' : markets.length}</span>
          </h2>
          {loading && <div className="flex items-center gap-2 py-10 text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading markets…</div>}
          {!loading && !markets.length && <p className="py-10 text-gray-400" data-testid="sport-empty">No open matches right now — new ones are added every few hours.</p>}
          {!loading && markets.length > 0 && (
            <ul className="rounded-xl border border-[#1e2a45] bg-[#0d1424] px-5">{markets.map((m) => <SportMarketRow key={m.id} market={m} />)}</ul>
          )}
        </section>
        <div className="mt-14 grid gap-8 md:grid-cols-2 max-w-5xl">
          {meta.sections.map((s) => (
            <section key={s.h2}>
              <h2 className="text-base md:text-lg font-semibold text-white mb-2">{s.h2}</h2>
              <p className="text-sm text-gray-400 leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
        <Faq faq={meta.faq} />
      </main>
      <Footer />
    </div>
  );
}
